import React, { useState } from 'react';

interface MailingListSignupProps {
  variant?: 'top' | 'middle' | 'bottom';
}

// TODO: Replace with your Mailchimp form URL
// To get your Mailchimp form URL:
// 1. Log into your Mailchimp account (thedanceflooradh@gmail.com)
// 2. Go to Audience > Signup forms > Embedded forms
// 3. Select your list
// 4. Copy the form action URL (it looks like: https://[dc].list-manage.com/subscribe/post?u=[USER_ID]&id=[LIST_ID])
// 5. Replace MAILCHIMP_FORM_URL below with your actual URL
//
// IMPORTANT: To collect phone numbers, you need to add a phone number field in Mailchimp:
// 1. Go to Audience > Manage Audience > Settings > List fields and |MERGE| tags
// 2. Click "Add A Field"
// 3. Select "Phone number" and save
// 4. The field will automatically be available as "PHONE" merge tag
const MAILCHIMP_FORM_URL = 'https://app.us19.list-manage.com/subscribe/post?u=d0157a460f742190b26f1ac4c&amp;id=7460c72d2b&amp;f_id=00eec2e1f0';

const MailingListSignup: React.FC<MailingListSignupProps> = ({ variant = 'top' }) => {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    // Check if Mailchimp URL is configured
    if (MAILCHIMP_FORM_URL.includes('YOUR_')) {
      alert('Please configure your Mailchimp form URL in the component code.');
      return;
    }

    setIsSubmitting(true);

    // Extract user ID and list ID from URL for bot protection field
    const urlParams = new URLSearchParams(MAILCHIMP_FORM_URL.split('?')[1]);
    const userId = urlParams.get('u');
    const listId = urlParams.get('id');

    // Create a form element and submit it directly to Mailchimp
    // Using a traditional form submission to avoid tracking protection issues
    const form = document.createElement('form');
    form.method = 'POST';
    form.action = MAILCHIMP_FORM_URL.replace('&amp;', '&');
    form.target = '_blank'; // Open in new tab to avoid tracking protection blocking
    form.style.display = 'none';

    // Add form fields
    const emailInput = document.createElement('input');
    emailInput.type = 'hidden';
    emailInput.name = 'EMAIL';
    emailInput.value = email;
    form.appendChild(emailInput);

    const fnameInput = document.createElement('input');
    fnameInput.type = 'hidden';
    fnameInput.name = 'FNAME';
    fnameInput.value = firstName;
    form.appendChild(fnameInput);

    const lnameInput = document.createElement('input');
    lnameInput.type = 'hidden';
    lnameInput.name = 'LNAME';
    lnameInput.value = lastName;
    form.appendChild(lnameInput);

    const phoneInput = document.createElement('input');
    phoneInput.type = 'hidden';
    phoneInput.name = 'PHONE';
    phoneInput.value = phone;
    form.appendChild(phoneInput);

    // Add bot protection field
    if (userId && listId) {
      const botInput = document.createElement('input');
      botInput.type = 'text';
      botInput.name = `b_${userId}_${listId}`;
      botInput.value = '';
      botInput.style.position = 'absolute';
      botInput.style.left = '-5000px';
      form.appendChild(botInput);
    }

    document.body.appendChild(form);
    
    // Submit form - opening in new tab helps avoid tracking protection
    form.submit();
    
    // Show success message after submission
    // Mailchimp will handle the redirect in the new tab
    setTimeout(() => {
      setIsSubmitted(true);
      setFirstName('');
      setLastName('');
      setEmail('');
      setPhone('');
      setIsSubmitting(false);
      
      // Clean up form after a delay
      setTimeout(() => {
        if (document.body.contains(form)) {
          document.body.removeChild(form);
        }
      }, 1000);
    }, 500);
  };

  if (variant === 'middle') {
    return (
      <section className="py-16 px-4 border-t border-dance-navy/20">
        <div className="max-w-4xl mx-auto">
          <div className="flex flex-col md:flex-row items-center justify-between gap-8">
            <div className="flex-1">
              <p className="text-lg text-dance-navy font-medium">
                Stay updated on opening events and early membership perks!
              </p>
            </div>
            <div className="flex-1">
              <form onSubmit={handleSubmit} className="flex flex-col gap-4">
                <div className="flex gap-4">
                  <input
                    type="text"
                    placeholder="First Name"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    className="flex-1 px-4 py-3 border border-dance-navy/30 rounded-sm focus:outline-none focus:border-dance-navy"
                    required
                  />
                  <input
                    type="text"
                    placeholder="Last Name"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    className="flex-1 px-4 py-3 border border-dance-navy/30 rounded-sm focus:outline-none focus:border-dance-navy"
                  />
                </div>
                <div className="flex gap-4">
                  <input
                    type="email"
                    placeholder="Your email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="flex-1 px-4 py-3 border border-dance-navy/30 rounded-sm focus:outline-none focus:border-dance-navy"
                    required
                  />
                  <input
                    type="tel"
                    placeholder="Phone Number"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    className="flex-1 px-4 py-3 border border-dance-navy/30 rounded-sm focus:outline-none focus:border-dance-navy"
                  />
                </div>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="bg-black text-white px-8 py-3 font-semibold tracking-wide hover:bg-dance-navy transition-colors duration-300 rounded-sm disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? 'SUBMITTING...' : 'SUBMIT'}
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>
    );
  }

  if (variant === 'bottom') {
    return (
      <section className="py-16 px-4 border-t border-dance-navy/20">
        <div className="max-w-4xl mx-auto text-center">
          <h3 className="text-2xl md:text-3xl font-serif font-bold text-dance-navy mb-4">
            Don't miss your chance to be part of Austin's new home for dancers.
          </h3>
          <div className="flex flex-col md:flex-row items-center justify-center gap-4 mt-8">
            <p className="text-lg text-dance-navy font-medium">
              Follow the development of The Dance Floor. Sign up for updates, exclusive invites, and notifications about new events.
            </p>
            <button className="bg-black text-white px-8 py-3 font-semibold tracking-wide hover:bg-dance-navy transition-colors duration-300 rounded-sm">
              SUBMIT
            </button>
          </div>
        </div>
      </section>
    );
  }

  // Top variant (default)
  return (
    <section id="mailing-list" className="py-20 px-4 bg-gradient-to-br from-dance-navy to-dance-navy/90">
      <div className="max-w-5xl mx-auto text-center">
        <div className="mb-12">
          <h3 className="text-3xl md:text-4xl font-serif font-bold text-white mb-6 tracking-wide">
            Join Our Community
          </h3>
          <p className="text-xl text-white/90 font-light leading-relaxed max-w-3xl mx-auto">
          Follow the development of The Dance Floor. Sign up for updates, exclusive invites, and notifications about new events.
          </p>
        </div>
        
        {isSubmitted ? (
          <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 max-w-md mx-auto">
            <div className="text-white font-medium text-lg">
              Thank you for signing up! We'll be in touch soon.
            </div>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="max-w-3xl mx-auto">
            <div className="flex flex-col sm:flex-row gap-4 mb-4">
              <input
                type="text"
                placeholder="First Name"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                className="flex-1 px-4 sm:px-6 py-3 sm:py-4 border border-white/30 rounded-xl focus:outline-none focus:border-dance-gold bg-white/10 backdrop-blur-sm text-white placeholder-white/70 focus:bg-white/20 transition-all duration-300 text-base"
                required
              />
              <input
                type="text"
                placeholder="Last Name"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                className="flex-1 px-4 sm:px-6 py-3 sm:py-4 border border-white/30 rounded-xl focus:outline-none focus:border-dance-gold bg-white/10 backdrop-blur-sm text-white placeholder-white/70 focus:bg-white/20 transition-all duration-300 text-base"
              />
            </div>
            <div className="flex flex-col sm:flex-row gap-4 mb-4">
              <input
                type="email"
                placeholder="Your Email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="flex-1 px-4 sm:px-6 py-3 sm:py-4 border border-white/30 rounded-xl focus:outline-none focus:border-dance-gold bg-white/10 backdrop-blur-sm text-white placeholder-white/70 focus:bg-white/20 transition-all duration-300 text-base"
                required
              />
              <input
                type="tel"
                placeholder="Phone Number"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                className="flex-1 px-4 sm:px-6 py-3 sm:py-4 border border-white/30 rounded-xl focus:outline-none focus:border-dance-gold bg-white/10 backdrop-blur-sm text-white placeholder-white/70 focus:bg-white/20 transition-all duration-300 text-base"
              />
            </div>
            <button
              type="submit"
              disabled={isSubmitting}
              className="bg-dance-gold text-dance-navy px-6 sm:px-8 py-3 sm:py-4 font-semibold tracking-wide hover:bg-dance-terracotta transition-all duration-300 rounded-xl shadow-lg hover:shadow-xl transform hover:-translate-y-1 text-base w-full sm:w-auto disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
            >
              {isSubmitting ? 'SUBMITTING...' : 'SUBMIT'}
            </button>
          </form>
        )}
      </div>
    </section>
  );
};

export default MailingListSignup;
